// Tender submission export — the cost model written out as the workbook that goes
// into the tender pack.
//
// Four sheets, in the order a panel reads them:
//   Price Submission — the four priced lines, current rate against proposed
//   Cost Base        — the annual build-up, every line traceable to an assumption
//   Assumptions      — every input that produced those costs, named and valued
//   Vehicle Options  — the same model run on each van, so the choice is evidenced
//
// Kept out of the page so the workbook can be built and checked in Node (transform
// with @babel/core preset-env, then _compile — the same as peakExport.js).
import * as XLSX from 'xlsx';
import { LINE_DEFS } from './tenderModel';

const money = (v) => (Number.isFinite(Number(v)) ? Number(Number(v).toFixed(2)) : '');
const rate4 = (v) => (Number.isFinite(Number(v)) ? Number(Number(v).toFixed(4)) : '');

export function buildSubmissionWorkbook(model, run, compare) {
  if (!run) throw new Error('Nothing to export — the model has not been run');
  const wb = XLSX.utils.book_new();
  const p = run.pricing;
  const c = run.costs;
  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  const vehicleLabel = (c.vehicles.profile && c.vehicles.profile.label) || run.vehicleKey;

  // ── Sheet 1: the submission ────────────────────────────────────────────────
  const sub = [
    ['Price Submission — operating cost test run'],
    [model.name || 'Tender model'],
    ['Prepared', today],
    ['Basis', `${model.margin.pct}% profit on ${model.margin.basis === 'cost' ? 'cost' : 'revenue'} · ${vehicleLabel} × ${model.fleet.vans}`],
    ['Period', `One year — ${Math.round(run.volumes.days)} delivery days (${model.ops.weekdayDays} weekdays, ${model.ops.saturdayDays} Saturdays)`],
    ['All rates ex GST'],
    [],
    ['Line', 'Unit', 'Annual volume', 'Current rate', 'Proposed rate', 'Uplift %', 'Current revenue', 'Proposed revenue'],
  ];
  p.lines.filter((l) => l.include).forEach((l) => sub.push([
    l.label, l.unit, Math.round(l.volume), rate4(l.currentRate), rate4(l.proposedRate),
    Number(l.upliftPct.toFixed(2)), money(l.currentRevenue), money(l.proposedRevenue),
  ]));
  sub.push([]);
  sub.push(['Total revenue', '', '', '', '', Number(p.blendedUpliftPct.toFixed(2)), money(p.currentRevenue), money(p.proposedRevenue)]);
  sub.push(['Total operating cost', '', '', '', '', '', money(c.total), money(c.total)]);
  sub.push(['Profit', '', '', '', '', '', money(p.currentProfit), money(p.proposedProfit)]);
  sub.push(['Margin %', '', '', '', '', '', Number(p.currentMarginPct.toFixed(2)), Number(p.proposedMarginPct.toFixed(2))]);
  sub.push([]);
  sub.push(['Cost per article (parcels + Express)', rate4(run.costPerParcel)]);
  sub.push(['Other revenue not priced here', money(run.turnover.other), (model.otherRevenue && model.otherRevenue.label) || '']);
  sub.push(['Total company turnover, ex GST', money(run.turnover.total)]);
  sub.push(['Cost per driver day', money(run.costPerDriverDay)]);
  if (model.notes) { sub.push([]); sub.push(['Notes', model.notes]); }
  const wsSub = XLSX.utils.aoa_to_sheet(sub);
  wsSub['!cols'] = [{ wch: 34 }, { wch: 13 }, { wch: 15 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, wsSub, 'Price Submission');

  // ── Sheet 2: the cost base ─────────────────────────────────────────────────
  const L = c.labour; const V = c.vehicles; const F = c.fuel; const E = c.equipment;
  const cost = [
    ['Annual operating cost base'],
    [`${vehicleLabel} · ${model.fleet.vans} vans on the road + ${model.fleet.spareVans} spare · ${Math.round(L.driverDays).toLocaleString()} driver days`],
    [],
    ['DRIVER LABOUR', 'Basis', 'Annual $'],
    ['Wages — casual Grade 2', `${Math.round(L.weekdayDriverDays).toLocaleString()} weekday + ${Math.round(L.satDriverDays).toLocaleString()} Saturday driver days`, money(L.wages)],
    ['Superannuation', `${model.labour.superPct}% of wages`, money(L.super)],
    ['Workers compensation', `${model.labour.workersCompPct}% of wages + super`, money(L.workersComp)],
    ['Payroll tax', model.labour.payrollTaxOn ? `${model.labour.payrollTaxPct}% above $${Number(model.labour.payrollTaxThreshold).toLocaleString()}` : 'not applied', money(L.payrollTax)],
    ['Labour subtotal', `$${L.perDriverDay.toFixed(2)} per driver day · $${L.effectiveHourly.toFixed(2)} effective hourly`, money(L.total)],
    [],
    ['VEHICLES', `per van $${V.perVan.total.toFixed(0)} × ${V.totalVans}`, 'Annual $'],
    [model.fleet.ownership === 'lease' ? 'Lease' : 'Depreciation', model.fleet.ownership === 'lease' ? `$${model.fleet.leasePerVanPerMonth}/month` : `${model.fleet.termYears} yr straight line to ${V.profile.residualPct}% residual`, money((model.fleet.ownership === 'lease' ? V.perVan.ownership : V.perVan.depreciation) * V.totalVans)],
    ...(model.fleet.ownership === 'lease' ? [] : [['Finance interest', `${model.fleet.financeRatePct}% on average balance`, money(V.perVan.interest * V.totalVans)]]),
    ['Registration & CTP', '', money(V.perVan.regoCtp * V.totalVans)],
    ['Insurance', '', money(V.perVan.insurance * V.totalVans)],
    ['Servicing', `${V.perVan.servicesPerYear.toFixed(1)} services/van/yr at ${Math.round(V.kmPerVanYear).toLocaleString()} km`, money(V.perVan.servicing * V.totalVans)],
    ['Tyres', `${V.profile.tyreLifeKm.toLocaleString()} km per set`, money(V.perVan.tyres * V.totalVans)],
    ['Repairs & maintenance', 'unscheduled allowance', money(V.perVan.repairs * V.totalVans)],
    ['Vehicle subtotal', '', money(V.total)],
    [],
    ['FUEL', `${F.adr} L/100km ADR + ${model.fuel.stopStartUpliftPct}% stop-start = ${F.effective.toFixed(1)} L/100km`, 'Annual $'],
    ['Driving', `${model.fuel.kmPerDay} km/day → ${F.drivingLitresPerDay.toFixed(2)} L`, ''],
    ['Idling', `${model.fuel.idleHoursPerDay} h/day at ${model.fuel.idleLitresPerHour} L/h → ${F.idleLitresPerDay.toFixed(2)} L`, ''],
    ['All-in consumption', `${F.allInL100.toFixed(1)} L/100km · ${F.litresPerDay.toFixed(2)} L per van per day`, ''],
    ['Fuel subtotal', `${Math.round(F.litresPerYear).toLocaleString()} L at $${model.fuel.dieselPerLitre}/L`, money(F.total)],
    [],
    ['EQUIPMENT & ROAD COSTS', '', 'Annual $'],
    ['Mobiles', `${E.drivers} drivers × $${model.equipment.mobilePerMonth}/month`, money(E.mobiles)],
    ['Trolleys', `$${model.equipment.trolleyCost} over ${model.equipment.trolleyLifeYears} years`, money(E.trolleys)],
    ['Parking & tolls', `$${model.equipment.parkingTollsPerDay} per driver day`, money(E.parkingTolls)],
    ['Fines', `$${model.equipment.finesPerVanPerYear} per van per year`, money(E.fines)],
    ...E.rows.map((r) => [r.label, '', money(r.total)]),
    ['Equipment subtotal', '', money(E.total)],
    [],
    ['INDIRECT / OVERHEAD', '', 'Annual $'],
    ...c.overheads.rows.map((r) => [r.label, '', money(r.annual)]),
    ['Overhead subtotal', '', money(c.overheads.total)],
    [],
    ['TOTAL OPERATING COST', '', money(c.total)],
  ];
  const wsCost = XLSX.utils.aoa_to_sheet(cost);
  wsCost['!cols'] = [{ wch: 32 }, { wch: 52 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, wsCost, 'Cost Base');

  // ── Sheet 3: assumptions ───────────────────────────────────────────────────
  const a = [['Assumptions'], ['Every figure below is an input — change it and the submission moves'], []];
  const put = (section, rows) => { a.push([section]); rows.forEach((r) => a.push(['', r[0], r[1], r[2] || ''])); a.push([]); };
  put('Operations', [
    ['Weekday operating days', model.ops.weekdayDays, 'Mon–Fri less NSW public holidays'],
    ['Saturday operating days', model.ops.saturdayDays],
    ['Drivers per weekday', model.ops.driversPerDay],
    ['Drivers per Saturday', model.ops.satDriversPerDay],
    ['Hours per shift', model.ops.hoursPerShift],
    ['Overtime hours per shift', model.ops.otHoursPerShift],
    ['Saturday hours per shift', model.ops.satHoursPerShift],
  ]);
  put('Labour — Road Transport and Distribution Award 2020 [MA000038] Grade 2', [
    ['Base hourly', model.labour.baseRate],
    ['Casual loading %', model.labour.casualLoadingPct],
    ['Casual ordinary hourly', L.rates.casual.toFixed(2)],
    ['Saturday penalty %', model.labour.satPenaltyPct],
    ['Casual Saturday hourly', L.rates.saturday.toFixed(2)],
    ['Overtime first 2 h %', model.labour.otFirst2Pct],
    ['Overtime thereafter %', model.labour.otAfterPct],
    ['Superannuation %', model.labour.superPct],
    ['Workers compensation %', model.labour.workersCompPct],
    ['Payroll tax %', model.labour.payrollTaxPct],
    ['Payroll tax threshold', model.labour.payrollTaxThreshold],
  ]);
  put('Vehicles', [
    ['Vehicle', vehicleLabel],
    ['Vans on the road', model.fleet.vans],
    ['Spare vans', model.fleet.spareVans],
    ['Capital cost', V.profile.capex, 'drive away'],
    ['Residual %', V.profile.residualPct],
    ['Term (years)', model.fleet.termYears],
    ['Finance rate %', model.fleet.financeRatePct],
    ['Service interval', `${V.profile.serviceIntervalKm} km / ${V.profile.serviceIntervalMonths} months`],
    ['Service cost', V.profile.serviceCost],
    ['Tyre set / life km', `${V.profile.tyreSetCost} / ${V.profile.tyreLifeKm}`],
    ['Rego & CTP', V.profile.regoCtp],
    ['Insurance', V.profile.insurance],
    ['Repairs allowance', V.profile.repairs],
  ]);
  put('Fuel', [
    ['Km per van per day', model.fuel.kmPerDay],
    ['ADR combined L/100km', V.profile.l100],
    ['Stop-start uplift %', model.fuel.stopStartUpliftPct, 'CBD kerb-to-door delivery, not a highway cycle'],
    ['Effective L/100km', F.effective.toFixed(2)],
    ['Idle hours per day', model.fuel.idleHoursPerDay],
    ['Idle litres per hour', model.fuel.idleLitresPerHour],
    ['All-in L/100km', F.allInL100.toFixed(2)],
    ['Diesel $/L', model.fuel.dieselPerLitre],
  ]);
  put('Equipment', [
    ['Mobile $/driver/month', model.equipment.mobilePerMonth],
    ['Trolley cost / life yrs', `${model.equipment.trolleyCost} / ${model.equipment.trolleyLifeYears}`],
    ['Parking & tolls $/driver day', model.equipment.parkingTollsPerDay],
    ['Fines $/van/year', model.equipment.finesPerVanPerYear],
    ...(model.equipment.rows || []).map((r) => [r.label, r.perDriverPerYear, 'per driver per year']),
  ]);
  put('Volumes — one year', LINE_DEFS.map((d) => {
    const cfg = model.lines[d.key];
    return [d.label, `${Number(cfg.volPerYear).toLocaleString()} ${d.unit}s a year`, `current rate ${cfg.currentRate}`];
  }));
  const wsA = XLSX.utils.aoa_to_sheet(a);
  wsA['!cols'] = [{ wch: 2 }, { wch: 44 }, { wch: 22 }, { wch: 46 }];
  XLSX.utils.book_append_sheet(wb, wsA, 'Assumptions');

  // ── Sheet 4: vehicle options ───────────────────────────────────────────────
  if (compare) {
    const keys = Object.keys(compare);
    const row = (label, pick) => [label, ...keys.map((k) => pick(compare[k]))];
    const veh = [
      ['Vehicle options — the same model, the only change is the van'],
      [],
      ['', ...keys.map((k) => (compare[k].costs.vehicles.profile.label || k))],
      row('Capital cost per van', (r) => money(r.costs.vehicles.profile.capex)),
      row('Residual %', (r) => r.costs.vehicles.profile.residualPct),
      row('Ownership cost per van per year', (r) => money(r.costs.vehicles.perVan.ownership)),
      row('Servicing per van per year', (r) => money(r.costs.vehicles.perVan.servicing)),
      row('Tyres per van per year', (r) => money(r.costs.vehicles.perVan.tyres)),
      row('Repairs per van per year', (r) => money(r.costs.vehicles.perVan.repairs)),
      row('Total per van per year', (r) => money(r.costs.vehicles.perVan.total)),
      [],
      row('ADR L/100km', (r) => r.costs.fuel.adr),
      row('All-in L/100km', (r) => Number(r.costs.fuel.allInL100.toFixed(2))),
      row('Fuel per van per year', (r) => money(r.costs.fuel.costPerVanYear)),
      [],
      row('Fleet cost — vehicles', (r) => money(r.costs.vehicles.total)),
      row('Fleet cost — fuel', (r) => money(r.costs.fuel.total)),
      row('TOTAL OPERATING COST', (r) => money(r.costs.total)),
      row('Parcel rate required', (r) => rate4(r.pricing.lines[0].proposedRate)),
      row('Blended uplift %', (r) => Number(r.pricing.blendedUpliftPct.toFixed(2))),
      row('Margin at current rates %', (r) => Number(r.pricing.currentMarginPct.toFixed(2))),
    ];
    const wsV = XLSX.utils.aoa_to_sheet(veh);
    wsV['!cols'] = [{ wch: 36 }, ...keys.map(() => ({ wch: 24 }))];
    XLSX.utils.book_append_sheet(wb, wsV, 'Vehicle Options');
  }

  return wb;
}

export function exportSubmission(model, run, compare) {
  const wb = buildSubmissionWorkbook(model, run, compare);
  const stamp = new Date().toISOString().slice(0, 10);
  const name = `Tender cost model - ${(model.name || 'test run').replace(/[\\/:*?"<>|]/g, '-')} - ${stamp}.xlsx`;
  XLSX.writeFile(wb, name);
  return name;
}
